import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  IconButton,
  Input,
  Select,
  Text,
  Textarea,
  useToast,
  VStack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FiTrash } from "react-icons/fi";
import axiosInstance from "../../config/axiosInstance";

const emptyOrder = {
  item: "",
  color: "",
  design: "",
  thread: "",
  lace: "",
  image: null,
  deliveryDate: "",
  tailor: "",
  quantity: 1, // Add quantity field
};

const emptyMeasurement = {
  length: "",
  width: "",
  hip: "",
  neck: "",
  chest: "",
  sleeve: "",
  waist: "",
  etc: "",
  status: "in-progress",
  description: "",
};

const initialFormData = {
  billNumber: "",
  name: "",
  phone: "",
  email: "",
  amount: "",
  paid: "",
};

const validateText = (val) => /^[a-zA-Z0-9\s]*$/.test(val);
const validateNumber = (val) => val === "" || /^\d+$/.test(val);
const validateEmail = (email) =>
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email.trim());

export default function NewCustomer() {
  const toast = useToast();
  const [tailors, setTailors] = useState([]);
  const [sets, setSets] = useState([
    { order: { ...emptyOrder }, measurement: { ...emptyMeasurement } },
  ]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [formData, setFormData] = useState({ ...initialFormData });

  useEffect(() => {
    const fetchMasters = async () => {
      try {
        const res = await axiosInstance.get("/api/users/roles/list");
        setTailors(res.data);
      } catch {
        toast({ title: "Failed to load tailors", status: "error" });
      }
    };
    fetchMasters();
  }, [toast]);

  const addSet = () => {
    if (sets.length >= 6) {
      toast({ title: "Max 6 items allowed", status: "warning" });
      return;
    }
    setSets([
      ...sets,
      { order: { ...emptyOrder }, measurement: { ...emptyMeasurement } },
    ]);
  };

  const deleteSet = (index) => {
    setSets(sets.filter((_, i) => i !== index));
  };

  const handleQuantityChange = (index, value) => {
    const updated = [...sets];
    updated[index].order.quantity = parseInt(value) || 1;
    setSets(updated);
  };

  const handleSave = async () => {
    setSaveLoading(true);

    // Validate only these three required fields
    if (!formData.billNumber) {
      showError("Bill number is required.");
      return;
    }
    
    if (!formData.phone || formData.phone.length !== 10) {
      showError("Phone must be exactly 10 digits.");
      return;
    }

    // Optional field validation
    if (formData.email && !validateEmail(formData.email)) {
      showError("Please enter a valid email.");
      return;
    }

    // Validate each order set (all fields optional except tailor and delivery date)
    for (let idx = 0; idx < sets.length; idx++) {
      const { order } = sets[idx];

      if (!order.deliveryDate) {
        showError(`Delivery date in Order ${idx + 1} is required.`);
        return;
      }
      if (!order.tailor) {
        showError(`Tailor is required in Order ${idx + 1}.`);
        return;
      }
    }

    try {
      // Create order details with quantity field instead of expanding
      const orderDetails = sets.map(({ order, measurement }) => ({
        ...order,
        quantity: order.quantity || 1, // Ensure quantity is included
        measurements: {
          ...measurement,
          status: "in-progress",
          etc: measurement.etc || "0",
        },
        description: measurement.description,
      }));

      const payload = new FormData();
      Object.entries(formData).forEach(([key, val]) =>
        payload.append(key, val)
      );
      payload.append(
        "payment",
        JSON.stringify({
          amount: formData.amount || 0,
          paid: formData.paid || 0,
        })
      );
      payload.append("orderDetails", JSON.stringify(orderDetails));

      sets.forEach(({ order }) => {
        if (order.image) payload.append("files", order.image);
      });

      await axiosInstance.post("/api/orders", payload);
      
      // Calculate total items for the success message
      const totalItems = sets.reduce((total, set) => total + (set.order.quantity || 1), 0);
      toast({ 
        title: `Order saved successfully with ${totalItems} items across ${sets.length} product types`, 
        status: "success" 
      });

      resetForm();
    } catch (err) {
      toast({ title: "Failed to create order", status: "error" });
    } finally {
      setSaveLoading(false);
    }
  };

  const showError = (message) => {
    toast({ title: message, status: "warning" });
    setSaveLoading(false);
  };

  const resetForm = () => {
    setFormData({ ...initialFormData });
    setSets([
      { order: { ...emptyOrder }, measurement: { ...emptyMeasurement } },
    ]);
  };

  const orderFields = [
    { name: "item", label: "Item" },
    { name: "color", label: "Color" },
    { name: "design", label: "Design" },
    { name: "thread", label: "Thread" },
    { name: "lace", label: "Lace" },
  ];

  return (
    <Box p={{ base: 4, md: 8 }} maxW="1200px" mx="auto">
      <Text fontSize="2xl" fontWeight="bold" mb={4} color="#E83733">
        New Order
      </Text>

      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
        <FormControl isRequired>
          <FormLabel>Bill Number</FormLabel>
          <Input
            value={formData.billNumber}
            onChange={(e) =>
              setFormData({
                ...formData,
                billNumber: e.target.value.toUpperCase(),
              })
            }
            placeholder="Enter bill number"
          />
        </FormControl>

        <FormControl >
          <FormLabel>Name</FormLabel>
          <Input
            value={formData.name}
            onChange={(e) => {
              if (validateText(e.target.value))
                setFormData({ ...formData, name: e.target.value });
            }}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>Phone</FormLabel>
          <Input
            value={formData.phone}
            onChange={(e) => {
              if (validateNumber(e.target.value))
                setFormData({ ...formData, phone: e.target.value });
            }}
            maxLength={10}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Email</FormLabel>
          <Input
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
          />
        </FormControl>
      </Grid>

      <Box mt={8}>
        <Text fontSize="lg" fontWeight="semibold" mb={2} color="#E83733">
          Order & Measurement Details
        </Text>
        <VStack spacing={6} align="stretch">
          {sets.map((set, idx) => (
            <Box key={idx} borderWidth="1px" borderRadius="md" p={4}>
              <HStack justify="space-between" mb={2}>
                <Text fontWeight="bold" fontSize="lg" color="#E83733">
                  Product Type {idx + 1}
                </Text>
                {idx > 0 && (
                  <IconButton
                    icon={<FiTrash />}
                    size="sm"
                    colorScheme="red"
                    onClick={() => deleteSet(idx)}
                  />
                )}
              </HStack>
              
              {/* Quantity Input */}
              <FormControl mb={4}>
                <FormLabel>Quantity</FormLabel>
                <NumberInput 
                  min={1} 
                  max={10000} 
                  value={set.order.quantity}
                  onChange={(value) => handleQuantityChange(idx, value)}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                gap={4}
              >
                {orderFields.map(({ name, label }) => (
                  <FormControl key={name}>
                    <FormLabel>{label}</FormLabel>
                    {name === "item" ? (
                      <Select
                        value={set.order[name]}
                        onChange={(e) => {
                          const updated = [...sets];
                          updated[idx].order[name] = e.target.value;
                          setSets(updated);
                        }}
                      >
                        <option value="" disabled>
                          Select Item
                        </option>
                        <option value="2 pc Suit">2 pc Suit</option>
                        <option value="3 pc Suit">3 pc Suit</option>
                        <option value="Blouse">Blouse</option>
                        <option value="Coat">Coat</option>
                        <option value="Dhoti">Dhoti</option>
                        <option value="Kamij">Kamij</option>
                        <option value="Kurta">Kurta</option>
                        <option value="Kurta pyjama">Kurta pyjama</option>
                        <option value="L/C">L/C</option>
                        <option value="Lehanga">Lehanga</option>
                        <option value="Odni">Odni</option>
                        <option value="Pant">Pant</option>
                        <option value="Pyjama">Pyjama</option>
                        <option value="Rajputi">Rajputi</option>
                        <option value="Safa">Safa</option>
                        <option value="Shirt">Shirt</option>
                        <option value="Saree">Saree</option>
                        <option value="Chunni">Chunni</option>
                        <option value="Base">Base</option>
                        <option value="Lehanga+Blouse">Lehanga + Blouse</option>
                        <option value="L/C+Blouse">L/C + Blouse</option>
                        <option value="Suit">Suit</option>
                        <option value="Odni+Blouse">Odni + Blouse</option>
                        <option value="Shirt+Pant">Shirt + Pant</option>
                        <option value="Suti Lehanga">Suti Lehanga</option>
                        <option value="Dupatta">Dupatta </option>
                        <option value="Petticoat">Petticoat</option>
                        <option value="Poshak">Poshak</option>
                        <option value="Rajputi + BP">Rajputi + BP</option>
                        <option value="RFD Fabric">RFD Fabric</option>
                        <option value="RFD Odni">RFD Odni</option>
                        <option value="RFD Dupatta">RFD Dupatta</option>
                      </Select>
                    ) : (
                      <Input
                        value={set.order[name]}
                        onChange={(e) => {
                          const updated = [...sets];
                          updated[idx].order[name] = e.target.value;
                          setSets(updated);
                        }}
                      />
                    )}
                  </FormControl>
                ))}

                <FormControl>
                  <FormLabel>Image</FormLabel>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const updated = [...sets];
                      updated[idx].order.image = e.target.files[0];
                      setSets(updated);
                    }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Delivery Date</FormLabel>
                  <Input
                    type="date"
                    min={
                      new Date(Date.now() + 86400000)
                        .toISOString()
                        .split("T")[0]
                    }
                    value={set.order.deliveryDate}
                    onChange={(e) => {
                      const updated = [...sets];
                      updated[idx].order.deliveryDate = e.target.value;
                      setSets(updated);
                    }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Master</FormLabel>
                  <Select
                    value={set.order.tailor}
                    onChange={(e) => {
                      const updated = [...sets];
                      updated[idx].order.tailor = e.target.value;
                      setSets(updated);
                    }}
                  >
                    <option value="">Select Tailor</option>
                    {tailors.map((t) => (
                      <option key={t._id} value={t._id}>
                        {t.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Text mt={4} fontWeight="semibold" color="#E83733">
                Measurements
              </Text>

              <Grid
                templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }}
                gap={4}
              >
                {(() => {
                  const item = set.order.item?.toLowerCase();
                  const fields = ["lehanga", "l/c"].includes(item)
                    ? [
                        { name: "length", label: "Length (L) - in inches" },
                        { name: "width", label: "Width (K) - in inches" },
                        { name: "hip", label: "Hip (H) - in inches" },
                      ]
                    : item === "blouse"
                    ? [
                        { name: "neck", label: "Neck (G) - in inches" },
                        { name: "length", label: "Length (L) - in inches" },
                        { name: "chest", label: "Chest (C) - in inches" },
                        { name: "width", label: "Width (K) - in inches" },
                        { name: "sleeve", label: "Sleeve (A) - in inches" },
                      ]
                    : ["odni", "saree", "shirt", "pant"].includes(item)
                    ? [
                        { name: "length", label: "Length (L) - in inches" },
                        { name: "width", label: "Width (K) - in inches" },
                        { name: "hip", label: "Hip (H) - in inches" },
                      ]
                    : [
                        { name: "length", label: "Length (L) - in inches" },
                        { name: "width", label: "Width (K) - in inches" },
                        { name: "hip", label: "Hip (H) - in inches" },
                        { name: "etc", label: "Etc (optional)" },
                      ];

                  return fields.map(({ name, label }) => (
                    <FormControl key={name}>
                      <FormLabel>{label}</FormLabel>
                      <Input
                        value={set.measurement[name] || ""}
                        onChange={(e) => {
                          const updated = [...sets];
                          updated[idx].measurement[name] = e.target.value;
                          setSets(updated);
                        }}
                      />
                    </FormControl>
                  ));
                })()}
              </Grid>

              <FormControl mt={4}>
                <FormLabel>Description (Optional)</FormLabel>
                <Textarea
                  value={set.measurement.description}
                  onChange={(e) => {
                    const updated = [...sets];
                    updated[idx].measurement.description = e.target.value;
                    setSets(updated);
                  }}
                />
              </FormControl>
            </Box>
          ))}
        </VStack>

        {sets.length < 6 && (
          <Button mt={4} onClick={addSet} colorScheme="red" variant="outline">
            + Add Product Type
          </Button>
        )}
      </Box>

      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4} mt={8}>
        <FormControl>
          <FormLabel>Amount</FormLabel>
          <Input
            value={formData.amount}
            onChange={(e) => {
              if (validateNumber(e.target.value))
                setFormData({ ...formData, amount: e.target.value });
            }}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Paid</FormLabel>
          <Input
            value={formData.paid}
            onChange={(e) => {
              if (validateNumber(e.target.value))
                setFormData({ ...formData, paid: e.target.value });
            }}
          />
        </FormControl>
      </Grid>

      <Button
        mt={8}
        colorScheme="red"
        w="full"
        onClick={handleSave}
        isLoading={saveLoading}
        loadingText="Saving..."
      >
        Save Order
      </Button>
    </Box>
  );
}